import * as _ from "lodash";
import * as React from "react";
import { useMappedState } from "redux-react-hook";
import styled, { css } from "styled-components";

import { useDrop } from "src/core/drops/hooks";
import { InfoBar } from "src/library/components/InfoBar";
import { ITerminology } from "src/library/utils/string-manipulations/tokenize-string";
import { Address } from "src/library/components/Address";
import {
  EditPurchaseOrderNumber,
  AddPurchaseOrderNumber
} from "src/core/shared/components/PurchaseOrderNumber";
import { ListMode } from "src/core/lists/store/lists.reducer";
import { IOrderStatus } from "src/core/orders/api/orders.api";
import { usePriceVisibility } from "src/core/prices/hooks/use-price-visibility";
import { TotalPrice } from "src/core/prices/components/TotalPrice";
import EditListName from "src/core/shared/components/EditListName";
import { IStore } from "src/core/shared/store";
import { Link } from "src/library/components/Link";
import { totalWeight } from "src/core/orders/utils/orders";
import { Text } from "src/library/components/Text";
import { TimePicker } from "src/core/shared/components/Date/TimePicker";
import { Button } from "src/library/components/Button";
import { Icon } from "src/library/components/Icon";
import { Icons } from "src/library/components/Icon/types";

import useList from "../../hooks/useList";

interface IProps {
  isLoading: boolean;
  viewMode: ListMode;
  primaryAction?: JSX.Element | null;
  secondaryAction?: JSX.Element| null;
  terminology: ITerminology;
  orderStatuses: IOrderStatus[];
  scheduleViewSetting: "orders" | "schedule" | null;
  setPONumber: (PONumber: string) => void;
  editListName: (listName: string) => void;
}

const ListInfoSidebar = React.memo(
  ({
    isLoading,
    viewMode,
    primaryAction,
    secondaryAction,
    terminology,
    setPONumber,
    editListName
  }: IProps) => {
    const pricesVisible = usePriceVisibility();
    const { roleType } = useMappedState(({ global }: IStore) => ({ roleType: global.role ? global.role.type : null }));
    const [ { drop } ] = useDrop();
    const [ { currentList }, { setList } ] = useList();
    const data = currentList.data;
    const [ settingTime, setSettingTime ] = React.useState(false);

    const totalListWeight = React.useMemo(() => {
      return totalWeight(data);
    }, [ data ]);

    const items = React.useMemo(() => [
      {
        label: "Name",
        value:
        data && data.name ? (
          <EditListName
            isEditable={viewMode === "edit"}
            listName={data.name}
            editListName={editListName}
          />
        ) : "-"
      },

      {
        label: "Total quantity",
        value: data ? _.sumBy(data.listLines, listLine => listLine.quantity) : "-"
      },

      totalListWeight !== null ? {
        label: "Total weight",
        value: (
          <Text>
            {`${totalListWeight}kg`}
          </Text>
        )
      } : null,
      viewMode === "view" && data?.requestedDeliveryTime && {
        label: "Requested Delivery Time",
        value: (
          <Text>
            {data.requestedDeliveryTime}
          </Text>
        )
      },
      viewMode === "edit" && (data?.requestedDeliveryTime || drop?.groupedSettings.allow_delivery_time?.value?.allowed) && {
        label: "Requested Delivery Time",
        value: (
          !settingTime && !data?.requestedDeliveryTime ? (
            <Button
              onClick={() => {
                setSettingTime(true);
              }}
            >
              Set Time
            </Button>
          ) : (
            <StyledTimeWrapper>
              <TimePicker
                name="Default Time"
                timezone="GMT"
                value={data?.requestedDeliveryTime ?? drop?.groupedSettings.allow_delivery_time?.value?.defaultTime}
                setTime={value => {
                  if (data) {
                    setList({
                      ...currentList,
                      saved: false,
                      data: {
                        ...data,
                        requestedDeliveryTime: value
                      }
                    });
                  }
                }}
              />

              <Icon
                icon={Icons.CLOSE}
                onClick={() => {
                  if (data) {
                    setList({
                      ...currentList,
                      saved: false,
                      data: {
                        ...data,
                        requestedDeliveryTime: null
                      }
                    });
                    setSettingTime(false);
                  }
                }}
              />
            </StyledTimeWrapper>
          ))

      },
      {
        label: "Purchase Order No.",
        value:
          data && data.customerReference ? (
            <EditPurchaseOrderNumber
              isEditable={viewMode === "edit"}
              type="list"
              customerReference={data.customerReference}
              setPONumber={setPONumber}
            />
          ) : viewMode === "edit" ? (
            <AddPurchaseOrderNumber type="list" />
          ) : "-"
      },
      {
        label: `${terminology.customer.singular.text}`,
        value: data?.branch?.customer ? roleType === "admin" ? (
          <Link
            color="primary"
            textTransform="capitalize"
            type="underlined"
            to={`/admin/customers/${data?.branch?.customerId}/drops`}
          >
            {`${data.branch.customer.name} (${data.branch.customer.accountNumber})`}
          </Link>
        ) : `${data.branch.customer.name} (${data.branch.customer.accountNumber})` : "-"
      },
      {
        label: `${terminology.drop.singular.text}`,
        value: data?.branch ? roleType === "admin" ? (
          <Link
            color="primary"
            textTransform="capitalize"
            type="underlined"
            to={`/admin/customers/${data?.branch?.customerId}/drops/${data?.branchId}/lists`}
          >
            {`${data.branch?.name} (${data.branch.branchNumber})`}
          </Link>
        ) : `${data.branch?.name} (${data.branch.branchNumber})` : "-"
      },
      {
        label: "Delivery address",
        value: data?.branch ? <Address address={data.branch.address} /> : "-"
      }
    ], [
      currentList,
      data,
      drop?.groupedSettings.allow_delivery_time?.value?.allowed,
      drop?.groupedSettings.allow_delivery_time?.value?.defaultTime,
      editListName,
      roleType,
      setList,
      setPONumber,
      settingTime,
      terminology.customer.singular.text,
      terminology.drop.singular.text,
      totalListWeight,
      viewMode
    ]);

    if (pricesVisible) {
      items.splice(1, 0, {
        label: "Total",
        value: (data ? (
          <TotalPrice
            lines={data.listLines}
            id={data.id}
          />
        ) : "-")
      });
    }

    return (
      <InfoBar
        isLoading={isLoading}
        title={`${_.capitalize(terminology.list.singular.text)} Information`}
        primaryAction={primaryAction}
        secondaryAction={secondaryAction}
        items={_.compact(items)}
      />
    );
  }
);

const StyledTimeWrapper = styled.div`
  ${({ theme }) => css`
      display: flex;
      flex-direction: row;
      align-items: center;
      gap:${theme.spacing.padding.xs};

      svg:hover {
        cursor: pointer;
      }
  `}
`;

export default ListInfoSidebar;
